import React, { useEffect, useRef, useState } from "react";
import colors from '../ColorConfig/colorconfig';

export default function ServiceInfo({ onSearch, searchQuery: propSearchQuery }) {
  const [isVisible, setIsVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState(propSearchQuery || '');
  const [isExpanded, setIsExpanded] = useState(false);
  const sectionRef = useRef(null);
  const searchInputRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Start search bar expansion after a short delay
          setTimeout(() => setIsExpanded(true), 800);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Update local state when prop changes
  useEffect(() => {
    setSearchQuery(propSearchQuery || '');
  }, [propSearchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    const query = searchQuery.trim();
    if (query !== '') {
      if (onSearch) {
        onSearch(query);
      }
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Trigger search as user types
    if (onSearch) {
      onSearch(value.trim());
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    if (onSearch) {
      onSearch('');
    }
    searchInputRef.current.focus();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch(e);
    }
  };

  return (
    <div 
      className="py-2" 
      style={{ backgroundColor: colors.bodyBlack }}
      ref={sectionRef}
    >
      <div 
        className="d-flex flex-column align-items-center justify-content-center" 
        style={{ padding: isMobile ? '10px 10px' : '40px 20px', textAlign: 'center', maxWidth: '1200px', margin: '0 auto'}}
      >
        <h2 
          className={`fw-bold mb-4 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
          style={{ 
            color: colors.white,
            animationDelay: '0.3s',
            fontSize: isMobile ? '1.5rem' : '2.5rem',
            textAlign: isMobile ? 'left' : 'center',
            fontWeight: '700',
            lineHeight: '1.2'
          }}
        >
          Empowering Your Vision with Bespoke IT Solutions
        </h2>
        <h3 
          className={`lead mb-5 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
          style={{ 
            color: colors.white,
            animationDelay: '0.4s', 
            maxWidth: '800px',
            fontSize: isMobile ? '1rem' : '1.2rem',
             textAlign: isMobile ? 'left' : 'center',
            lineHeight: '1.5'
          }}
        >
          At IDLBAY Private Limited, we transform complex challenges into innovative, scalable, and efficient IT solutions that drive growth and ensure your business stays ahead.
        </h3>

        {/* Search Bar */}
        <div 
          className={`search-container ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
          style={{ 
            animationDelay: '0.6s',
            width: '100%',
            maxWidth: isExpanded ? (isMobile ? '100%' : '600px') : '100px',
            transition: 'max-width 1.5s ease-out',
            position: 'relative',
            margin: '0 auto'
          }}
        >
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-input-wrapper">
              <input
                ref={searchInputRef}
                type="text"
                placeholder={isExpanded ? "Search services by title or category..." : "Search..."}
                value={searchQuery}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                className="search-input"
                onFocus={() => setIsExpanded(true)}
                style={{
                  width: '100%',
                  padding: isMobile ? '12px 16px' : '14px 24px',
                  paddingRight: searchQuery ? '150px' : '120px',
                  fontSize: isMobile ? '14px' : '16px',
                  border: 'none',
                  borderRadius: '50px',
                  backgroundColor: colors.white,
                  color: colors.black,
                  outline: 'none',
                  transition: 'all 0.5s ease',
                  boxShadow: `0 4px 15px rgba(0, 0, 0, 0.2)`,
                  opacity: isExpanded ? 1 : 0,
                  transform: isExpanded ? 'scale(1)' : 'scale(0.8)'
                }}
              />
              
              {/* Clear button (shown when there's text) */}
              {searchQuery && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="clear-button"
                  style={{
                    position: 'absolute',
                    right: '2px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: `linear-gradient(135deg, ${colors.black} 0%, ${colors.black} 100%)`,
                    border: 'none',
                    borderRadius: '25px',
                    padding: isMobile ? '10px 18px' : '12px 24px',
                    color: colors.white,
                    fontSize: isMobile ? '14px' : '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.5s ease',
                    opacity: isExpanded ? 1 : 0,
                    pointerEvents: isExpanded ? 'auto' : 'none'
                  }}
                >
                  Clear
                </button>
              )}
              
              {/* Search button (shown when there's no text) */}
              {!searchQuery && (
                <button
                  type="submit"
                  className="search-button"
                  style={{
                    position: 'absolute',
                    right: '2px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: `linear-gradient(135deg, ${colors.black} 0%, ${colors.black} 100%)`,
                    border: 'none',
                    borderRadius: '25px',
                    padding: isMobile ? '10px 18px' : '12px 24px',
                    color: colors.white,
                    fontSize: isMobile ? '14px' : '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.5s ease',
                    opacity: isExpanded ? 1 : 0,
                    pointerEvents: isExpanded ? 'auto' : 'none'
                  }}
                >
                  Search
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      <style>{`
        .animate-fade-in-up {
          animation: fadeInUp 1s ease-out forwards;
        }
        
        .opacity-0 {
          opacity: 0;
        }
        
        @keyframes fadeInUp {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .search-input:focus {
          border-color: ${colors.black} !important;
          box-shadow: 0 0 0 3px ${colors.primary}20, 0 4px 20px rgba(0, 0, 0, 0.3) !important;
          transition: all 0.5s ease;
        }
        
        .search-input::placeholder {
          color: ${colors.gray};
          transition: color 0.5s ease;
        }
        
        .search-button:hover, .clear-button:hover {
          transform: translateY(-50%) scale(1.05);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
          transition: all 0.5s ease;
        }
        
        .search-container {
          position: relative;
          z-index: 1;
        }
        
        .search-form {
          position: relative;
        }
        
        .search-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        @media (max-width: 768px) {
          .search-input {
            padding: 16px 20px !important;
            padding-right: 110px !important;
            font-size: 14px !important;
          }
          
          .search-button, .clear-button {
            padding: 10px 20px !important;
            font-size: 14px !important;
          }
        }
      `}</style>
    </div>
  );
}